import React, {useState, useEffect, useCallback} from 'react';
import {
    Button, Callout, Dialog, DialogBody, DialogFooter,
    FormGroup, Icon, Menu, MenuItem, Popover, Spinner, Switch,
} from '@blueprintjs/core';
import {MultiSelect, Select} from '@blueprintjs/select';
import styled from 'styled-components';

import {dashboardReports, locations, scheduledReportJobs} from '/src/services/api';
import * as AuthAPI from '/src/services/auth-api';
import {errorToast, successToast} from '/src/utils/toaster';

const SEND_TYPE_ALL = 'email - all org users';
const SEND_TYPE_SPECIFIC = 'email - specific users';

const CADENCES = ['hourly', 'daily', 'weekly', 'monthly'];
const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const formatHour = (h) => {
    if (h === 0) return '12am';
    if (h === 12) return '12pm';
    return h > 12 ? `${h - 12}pm` : `${h}am`;
};

const ordinalSuffix = (n) => {
    const s = ['th', 'st', 'nd', 'rd'];
    const v = n % 100;
    return n + (s[(v - 20) % 10] || s[v] || s[0]);
};

const Section = styled.div`
    margin-bottom: 1em;
`;

const SectionHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    margin-bottom: 8px;
    padding-bottom: 6px;
    border-bottom: 1px solid #ced9e0;
    font-weight: 600;
`;

const ScheduleRow = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px;
`;

const initState = (job) => {
    if (!job) return {
        dashboardReportId: '',
        restrictToLocations: [],
        sendType: SEND_TYPE_ALL,
        toEmails: [],
        cadence: 'weekly',
        sendDay: 1,
        sendHour: 9,
        impersonationAuth0Id: '',
        scheduledSendingDisabled: false,
    };
    return {
        dashboardReportId: job.dashboardReportToImage?.dashboardReportId || '',
        restrictToLocations: job.dashboardReportToImage?.restrictToLocations || [],
        sendType: job.distribution?.sendType || SEND_TYPE_ALL,
        toEmails: job.distribution?.toEmails?.filter(Boolean) || [],
        cadence: job.schedule?.cadence || 'weekly',
        sendDay: job.schedule?.sendDay ?? 1,
        sendHour: job.schedule?.sendHour ?? 9,
        impersonationAuth0Id: job.impersonationAuth0Id || '',
        scheduledSendingDisabled: !!job.scheduledSendingDisabled,
    };
};

const buildPayload = (state, orgid) => ({
    jobType: 'dashboardReportToImage',
    organisation: orgid,
    impersonationAuth0Id: state.impersonationAuth0Id,
    scheduledSendingDisabled: state.scheduledSendingDisabled,
    distribution: {
        sendType: state.sendType,
        toEmails: state.sendType === SEND_TYPE_SPECIFIC ? state.toEmails : [],
    },
    schedule: {
        cadence: state.cadence,
        ...(['daily', 'weekly', 'monthly'].includes(state.cadence) ? {sendHour: state.sendHour} : {}),
        ...(['weekly', 'monthly'].includes(state.cadence) ? {sendDay: state.sendDay} : {}),
    },
    dashboardReportToImage: {
        dashboardReportId: state.dashboardReportId,
        restrictToLocations: state.restrictToLocations,
    },
});

const validate = (state) => {
    if (!state.impersonationAuth0Id) return 'Impersonation user is required';
    if (!state.dashboardReportId) return 'Dashboard Report is required';
    if (state.sendType === SEND_TYPE_SPECIFIC && !state.toEmails.length) return 'Select at least one recipient';
    if (!state.cadence) return 'Select a cadence';
    return null;
};

const ScheduleDialog = ({isOpen, onClose, orgid, job, onSaved}) => {
    const [state, setState] = useState(() => initState(job));
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const isEdit = !!job;
    const isCsv = job?.jobType === 'csvExtraction';

    // Reference data
    const [orgReports, setOrgReports] = useState(null);
    const [orgLocations, setOrgLocations] = useState(null);
    const [orgUsers, setOrgUsers] = useState(null);

    useEffect(() => {
        if (isOpen) {
            setState(initState(job));
            setError(null);
        }
    }, [isOpen, job]);

    // Fetch reference data when dialog opens
    useEffect(() => {
        if (!isOpen) return;

        dashboardReports.list().then(all => {
            setOrgReports(all.filter(r => r.organisation === orgid));
        }).catch(() => setOrgReports([]));

        locations.list({withinOrganisations: orgid}).then(locs => {
            setOrgLocations(locs);
        }).catch(() => setOrgLocations([]));

        Promise.all([
            AuthAPI.users.list(orgid),
            AuthAPI.users.listAdopted(orgid),
        ]).then(([usersRes, adoptedRes]) => {
            const allUsers = [
                ...(usersRes.data || []),
                ...(adoptedRes.data || []),
            ];
            setOrgUsers(allUsers);

            // Auto-default impersonation to the Aura reports user when creating
            if (!job) {
                const auraUser = allUsers.find(u =>
                    (u.email || '').endsWith('@auravision.ai') && /^reports\+/i.test(u.email)
                ) || allUsers.find(u =>
                    (u.email || '').endsWith('@auravision.ai')
                );
                if (auraUser) {
                    setState(prev => prev.impersonationAuth0Id ? prev : {...prev, impersonationAuth0Id: auraUser.auth0Id});
                }
            }
        }).catch(() => setOrgUsers([]));
    }, [isOpen, orgid, job]);

    const set = useCallback((patch) => setState(prev => ({...prev, ...patch})), []);

    const handleSave = useCallback(async () => {
        const err = validate(state);
        if (err) { setError(err); return; }
        setError(null);
        setLoading(true);
        try {
            const payload = buildPayload(state, orgid);
            if (isEdit) {
                await scheduledReportJobs.update(job.id, payload);
                successToast({message: 'Schedule updated'});
            } else {
                await scheduledReportJobs.create(payload);
                successToast({message: 'Schedule created'});
            }
            onSaved();
            onClose();
        } catch (e) {
            errorToast({message: e?.message ?? 'Failed to save'});
            setError(e?.message ?? 'Failed to save');
        } finally {
            setLoading(false);
        }
    }, [state, orgid, job, isEdit, onSaved, onClose]);

    const handleDelete = useCallback(async () => {
        if (!job) return;
        setLoading(true);
        try {
            await scheduledReportJobs.remove(job.id);
            successToast({message: 'Schedule deleted'});
            onSaved();
            onClose();
        } catch (e) {
            errorToast({message: e?.message ?? 'Failed to delete'});
        } finally {
            setLoading(false);
        }
    }, [job, onSaved, onClose]);

    const dataLoaded = orgReports && orgLocations && orgUsers;

    const selectedReport = orgReports?.find(r => r.id === state.dashboardReportId);
    const selectedUser = orgUsers?.find(u => u.auth0Id === state.impersonationAuth0Id);
    const selectedLocations = orgLocations?.filter(l => state.restrictToLocations.includes(l.id)) || [];

    return (
        <Dialog
            isOpen={isOpen}
            onClose={onClose}
            title={isEdit ? (isCsv ? 'CSV Schedule (read-only)' : 'Edit Scheduled Report') : 'New Scheduled Report'}
            icon={isEdit ? 'edit' : 'add'}
            style={{width: 600}}
        >
            <DialogBody>
                {!dataLoaded ? (
                    <div style={{display: 'flex', justifyContent: 'center', padding: '2em'}}>
                        <Spinner size={24} />
                    </div>
                ) : (
                    <>
                        {isCsv && (
                            <Callout intent="warning" icon="info-sign" style={{marginBottom: '1em'}}>
                                CSV extraction schedules cannot be edited. You can only delete them.
                            </Callout>
                        )}

                        {/* Enabled toggle */}
                        {!isCsv && (
                            <Section>
                                <Switch
                                    checked={!state.scheduledSendingDisabled}
                                    onChange={() => set({scheduledSendingDisabled: !state.scheduledSendingDisabled})}
                                    label="Enabled"
                                    style={{margin: 0}}
                                />
                            </Section>
                        )}

                        {/* Report */}
                        <Section>
                            <SectionHeader>
                                <Icon icon="document" intent="primary" />
                                Report
                            </SectionHeader>
                            {isCsv ? (
                                <div style={{fontSize: 13, color: '#5c7080'}}>
                                    <div><strong>Type:</strong> CSV Extraction</div>
                                    <div><strong>Path:</strong> {job.csvExtraction?.dashboardPath || '—'}</div>
                                    {job.csvExtraction?.csvDownloadButtonSelector && (
                                        <div><strong>Selector:</strong> {job.csvExtraction.csvDownloadButtonSelector}</div>
                                    )}
                                </div>
                            ) : (
                                <>
                                    <FormGroup label="Dashboard Report">
                                        <Select
                                            items={orgReports}
                                            itemPredicate={(query, r) => r.name.toLowerCase().includes(query.toLowerCase())}
                                            itemRenderer={(r, {handleClick, modifiers}) => (
                                                <MenuItem
                                                    key={r.id}
                                                    text={r.name}
                                                    label={r.status}
                                                    onClick={handleClick}
                                                    active={modifiers.active}
                                                    selected={state.dashboardReportId === r.id}
                                                />
                                            )}
                                            onItemSelect={(r) => set({dashboardReportId: r.id})}
                                            popoverProps={{minimal: true, matchTargetWidth: true}}
                                            menuProps={{style: {maxHeight: 450, overflowY: 'auto'}}}
                                            fill
                                        >
                                            <Button
                                                fill
                                                text={selectedReport ? selectedReport.name : 'Select a report...'}
                                                endIcon="caret-down"
                                            />
                                        </Select>
                                    </FormGroup>
                                    <FormGroup label="Restrict to Locations" helperText="Leave empty to include all locations">
                                        <MultiSelect
                                            items={orgLocations}
                                            selectedItems={selectedLocations}
                                            itemPredicate={(query, loc) => loc.name.toLowerCase().includes(query.toLowerCase())}
                                            itemRenderer={(loc, {handleClick, modifiers}) => (
                                                <MenuItem
                                                    key={loc.id}
                                                    text={loc.name}
                                                    onClick={handleClick}
                                                    active={modifiers.active}
                                                    selected={state.restrictToLocations.includes(loc.id)}
                                                    icon={state.restrictToLocations.includes(loc.id) ? 'tick' : 'blank'}
                                                />
                                            )}
                                            tagRenderer={(loc) => loc.name}
                                            onItemSelect={(loc) => {
                                                const has = state.restrictToLocations.includes(loc.id);
                                                set({
                                                    restrictToLocations: has
                                                        ? state.restrictToLocations.filter(id => id !== loc.id)
                                                        : [...state.restrictToLocations, loc.id],
                                                });
                                            }}
                                            onRemove={(loc) => {
                                                set({restrictToLocations: state.restrictToLocations.filter(id => id !== loc.id)});
                                            }}
                                            tagInputProps={{placeholder: 'All locations'}}
                                            popoverProps={{minimal: true, matchTargetWidth: true}}
                                            menuProps={{style: {maxHeight: 450, overflowY: 'auto'}}}
                                            fill
                                        />
                                    </FormGroup>
                                </>
                            )}
                        </Section>

                        {/* Distribution */}
                        <Section>
                            <SectionHeader>
                                <Icon icon="envelope" intent="primary" />
                                Distribution
                            </SectionHeader>
                            {isCsv ? (
                                <div style={{fontSize: 13, color: '#5c7080'}}>
                                    {job.distribution?.sendType === SEND_TYPE_ALL
                                        ? 'All org users'
                                        : (job.distribution?.toEmails || []).join(', ') || '—'}
                                </div>
                            ) : (
                                <div style={{display: 'flex', gap: '1em', alignItems: 'flex-start', flexDirection: 'column'}}>
                                    <Select
                                        items={[
                                            {value: SEND_TYPE_ALL, label: 'All org users'},
                                            {value: SEND_TYPE_SPECIFIC, label: 'Specific users'},
                                        ]}
                                        itemRenderer={(item, {handleClick, modifiers}) => (
                                            <MenuItem
                                                key={item.value}
                                                text={item.label}
                                                onClick={handleClick}
                                                active={modifiers.active}
                                                selected={state.sendType === item.value}
                                            />
                                        )}
                                        onItemSelect={(item) => set({sendType: item.value})}
                                        filterable={false}
                                        popoverProps={{minimal: true}}
                                    >
                                        <Button
                                            text={state.sendType === SEND_TYPE_ALL ? 'All org users' : 'Specific users'}
                                            endIcon="caret-down"
                                        />
                                    </Select>
                                    {state.sendType === SEND_TYPE_SPECIFIC && (
                                        <FormGroup label="Recipients" style={{width: '100%'}}>
                                            <MultiSelect
                                                items={orgUsers}
                                                selectedItems={orgUsers.filter(u => state.toEmails.includes(u.email))}
                                                itemPredicate={(query, u) => {
                                                    const q = query.toLowerCase();
                                                    return (u.email || '').toLowerCase().includes(q)
                                                        || (u.fullName || '').toLowerCase().includes(q);
                                                }}
                                                itemRenderer={(u, {handleClick, modifiers}) => (
                                                    <MenuItem
                                                        key={u.id}
                                                        text={u.fullName || u.email}
                                                        label={u.email}
                                                        onClick={handleClick}
                                                        active={modifiers.active}
                                                        selected={state.toEmails.includes(u.email)}
                                                        icon={state.toEmails.includes(u.email) ? 'tick' : 'blank'}
                                                    />
                                                )}
                                                tagRenderer={(u) => u.fullName || u.email}
                                                onItemSelect={(u) => {
                                                    const has = state.toEmails.includes(u.email);
                                                    set({
                                                        toEmails: has
                                                            ? state.toEmails.filter(e => e !== u.email)
                                                            : [...state.toEmails, u.email],
                                                    });
                                                }}
                                                onRemove={(u) => {
                                                    set({toEmails: state.toEmails.filter(e => e !== u.email)});
                                                }}
                                                tagInputProps={{placeholder: 'Select users...'}}
                                                popoverProps={{minimal: true, matchTargetWidth: true}}
                                            menuProps={{style: {maxHeight: 450, overflowY: 'auto'}}}
                                                fill
                                            />
                                        </FormGroup>
                                    )}
                                </div>
                            )}
                        </Section>

                        {/* Schedule */}
                        <Section>
                            <SectionHeader>
                                <Icon icon="timeline-events" intent="primary" />
                                Schedule
                            </SectionHeader>
                            {isCsv ? (
                                <div style={{fontSize: 13, color: '#5c7080'}}>
                                    {job.schedule?.cadence || '—'}
                                    {job.schedule?.sendHour != null && ` at ${formatHour(job.schedule.sendHour)} UTC`}
                                </div>
                            ) : (
                                <ScheduleRow>
                                    <Popover
                                        minimal
                                        content={
                                            <Menu>
                                                {CADENCES.map(c => (
                                                    <MenuItem
                                                        key={c}
                                                        text={c.charAt(0).toUpperCase() + c.slice(1)}
                                                        icon={state.cadence === c ? 'tick' : 'blank'}
                                                        onClick={() => set({cadence: c})}
                                                    />
                                                ))}
                                            </Menu>
                                        }
                                    >
                                        <Button text={state.cadence.charAt(0).toUpperCase() + state.cadence.slice(1)} endIcon="caret-down" />
                                    </Popover>

                                    {state.cadence === 'weekly' && (
                                        <>
                                            <span>on</span>
                                            <Popover
                                                minimal
                                                content={
                                                    <Menu>
                                                        {[1, 2, 3, 4, 5, 6, 0].map(d => (
                                                            <MenuItem
                                                                key={d}
                                                                text={DAY_NAMES[d]}
                                                                icon={state.sendDay === d ? 'tick' : 'blank'}
                                                                onClick={() => set({sendDay: d})}
                                                            />
                                                        ))}
                                                    </Menu>
                                                }
                                            >
                                                <Button text={DAY_NAMES[state.sendDay]} endIcon="caret-down" />
                                            </Popover>
                                        </>
                                    )}

                                    {state.cadence === 'monthly' && (
                                        <>
                                            <span>on the</span>
                                            <Popover
                                                minimal
                                                content={
                                                    <Menu>
                                                        {Array.from({length: 31}, (_, i) => i + 1).map(d => (
                                                            <MenuItem
                                                                key={d}
                                                                text={ordinalSuffix(d)}
                                                                icon={state.sendDay === d ? 'tick' : 'blank'}
                                                                onClick={() => set({sendDay: d})}
                                                            />
                                                        ))}
                                                    </Menu>
                                                }
                                            >
                                                <Button text={ordinalSuffix(state.sendDay)} endIcon="caret-down" />
                                            </Popover>
                                        </>
                                    )}

                                    {['daily', 'weekly', 'monthly'].includes(state.cadence) && (
                                        <>
                                            <span>at</span>
                                            <Popover
                                                minimal
                                                content={
                                                    <Menu>
                                                        {Array.from({length: 24}, (_, i) => i).map(h => (
                                                            <MenuItem
                                                                key={h}
                                                                text={formatHour(h)}
                                                                icon={state.sendHour === h ? 'tick' : 'blank'}
                                                                onClick={() => set({sendHour: h})}
                                                            />
                                                        ))}
                                                    </Menu>
                                                }
                                            >
                                                <Button text={formatHour(state.sendHour)} endIcon="caret-down" />
                                            </Popover>
                                            <span style={{color: '#5c7080', fontSize: 12}}>UTC</span>
                                        </>
                                    )}
                                </ScheduleRow>
                            )}
                        </Section>

                        {/* Impersonation */}
                        <Section>
                            <SectionHeader>
                                <Icon icon="person" intent="primary" />
                                Impersonation
                            </SectionHeader>
                            {isCsv ? (
                                <div style={{fontSize: 13, color: '#5c7080'}}>
                                    {job.impersonationAuth0Id || '—'}
                                </div>
                            ) : (
                                <FormGroup label="Run as user">
                                    <Select
                                        items={orgUsers}
                                        itemPredicate={(query, u) => {
                                            const q = query.toLowerCase();
                                            return (u.email || '').toLowerCase().includes(q)
                                                || (u.fullName || '').toLowerCase().includes(q)
                                                || (u.auth0Id || '').toLowerCase().includes(q);
                                        }}
                                        itemRenderer={(u, {handleClick, modifiers}) => (
                                            <MenuItem
                                                key={u.id}
                                                text={u.fullName || u.email}
                                                label={u.email}
                                                onClick={handleClick}
                                                active={modifiers.active}
                                                selected={state.impersonationAuth0Id === u.auth0Id}
                                            />
                                        )}
                                        onItemSelect={(u) => set({impersonationAuth0Id: u.auth0Id})}
                                        popoverProps={{minimal: true, matchTargetWidth: true}}
                                        menuProps={{style: {maxHeight: 450, overflowY: 'auto'}}}
                                        fill
                                    >
                                        <Button
                                            fill
                                            text={selectedUser ? (selectedUser.fullName || selectedUser.email) : 'Select a user...'}
                                            endIcon="caret-down"
                                        />
                                    </Select>
                                </FormGroup>
                            )}
                        </Section>

                        {error && <Callout intent="warning">{error}</Callout>}
                    </>
                )}
            </DialogBody>
            <DialogFooter actions={
                <div style={{display: 'flex', gap: '0.5em', width: '100%', justifyContent: isEdit ? 'space-between' : 'flex-end'}}>
                    {isEdit && (
                        <Button
                            intent="danger"
                            icon="trash"
                            text="Delete"
                            disabled={loading}
                            onClick={handleDelete}
                        />
                    )}
                    <div style={{display: 'flex', gap: '0.5em'}}>
                        <Button text={isCsv ? 'Close' : 'Cancel'} onClick={onClose} disabled={loading} />
                        {!isCsv && (
                            <Button
                                intent="primary"
                                text={loading ? 'Saving...' : (isEdit ? 'Save' : 'Create')}
                                disabled={loading || !dataLoaded}
                                onClick={handleSave}
                            />
                        )}
                    </div>
                </div>
            } />
        </Dialog>
    );
};

export {ScheduleDialog};
